/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/

import * as actions from ".";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Edits, Intro, LoadStatus } from "../../../types";

export interface UseWithIntros {
  intros: Record<string, Intro>;
  introEdits: Record<string, Edits<Intro>>;
  introLoadStatus: LoadStatus;
  loadAllIntros: () => Promise<Intro[]>;
  createIntro: (newData: Partial<Intro>) => Promise<Intro>;
  deleteIntro: (intro: Intro) => Promise<void>;
  updateIntro: (newData: Partial<Intro>) => Promise<Intro>;
}

export default function useWithIntros(): UseWithIntros {
  const { intros, introEdits, introLoadStatus } = useAppSelector(
    (state) => state.intros
  );
  const dispatch = useAppDispatch();

  async function loadAllIntros(): Promise<Intro[]> {
    if (introLoadStatus === LoadStatus.IN_PROGRESS) return [];
    return await dispatch(actions.loadIntros()).unwrap();
  }

  async function createIntro(data: Partial<Intro>): Promise<Intro> {
    return await dispatch(
      actions.addOrUpdateIntro({
        id: data.id || "",
        name: data.name || "",
        desc: data.desc || "",
        hidden: Boolean(data.hidden),
        deleted: Boolean(data.deleted),
        slides: data.slides || [],
      })
    ).unwrap();
  }

  async function deleteIntro(intro: Intro): Promise<void> {
    await dispatch(actions.deleteIntro(intro.id));
  }

  async function updateIntro(data: Partial<Intro>): Promise<Intro> {
    return await dispatch(actions.addOrUpdateIntro(data)).unwrap();
  }

  return {
    intros,
    introEdits,
    introLoadStatus,
    loadAllIntros,
    createIntro,
    deleteIntro,
    updateIntro,
  };
}
